/**
 * context/countsContext.tsx
 * The two sidebar badges — active-rule count (Rules) and pending-proposal count
 * (AI Proposals) — kept in one place so screens can bump them after a mutation
 * (toggle, approve, install) without prop-drilling or duplicate fetches.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { listRules } from '../services/rules/rules';
import { listProposals } from '../services/proposals/proposals';
import { useApp } from './appContext';

interface CountsValue {
  activeRuleCount: number;
  pendingCount: number;
  refresh: () => void;
}

const CountsContext = createContext<CountsValue>({ activeRuleCount: 0, pendingCount: 0, refresh: () => {} });

export function CountsProvider({ children }: { children: React.ReactNode }) {
  const { vertical } = useApp();
  const [activeRuleCount, setActiveRuleCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const refresh = useCallback(() => {
    listRules()
      .then((rules) => setActiveRuleCount(rules.filter((r) => r.status === 'active').length))
      .catch(() => undefined);
    listProposals()
      // Badge counts ACTIVE proposals only — out-of-date (stale) and rejected ones
      // live in their own tabs and shouldn't inflate the "to review" number.
      .then((p) => setPendingCount(p.filter((x) => !x.stale && x.status !== 'rejected').length))
      .catch(() => undefined);
  }, []);

  // Re-pull when the demo vertical swaps (seed dataset changes) and on mount.
  useEffect(() => {
    refresh();
  }, [refresh, vertical]);

  return (
    <CountsContext.Provider value={{ activeRuleCount, pendingCount, refresh }}>{children}</CountsContext.Provider>
  );
}

export const useCounts = () => useContext(CountsContext);
